import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import { CategoryAccount, BatchUpdateCategoryAccountEntry } from '../../models';
import { TsButtonModule, TsPageHeaderModule, TsSnackbarService } from 'tsui';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import {
  GridApi,
  IRowNode,
  RefreshCellsParams,
  RowDragEndEvent,
  RowDragLeaveEvent,
  RowDragMoveEvent,
} from 'ag-grid-enterprise';
import { WithPathAndDepth } from '../../../settings/teams/services/teams.service';
import { TsTableComponent } from 'tsui/lib/ts-table';
import { AssetManagementGridService } from '../../services/asset-management-grid.service';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Team } from '@core/models';

@Component({
  selector: 'asset-management-category-accounts-grid',
  standalone: true,
  imports: [TsPageHeaderModule, TsButtonModule, AgGridAngular, AsyncPipe],
  templateUrl: './asset-management-category-accounts-grid.component.html',
  styleUrl: '../../styles/asset-management-shared-styles.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetManagementCategoryAccountsGridComponent {
  @Input({ required: true }) set categoryAccounts(val: CategoryAccount[]) {
    this.rowData = this.flattenCategoryAccountsAndAddMetadata(val);
    this.initialRowData = this.deepCloneCategoryAccounts(this.rowData);
  }

  @Input() set loading(val: boolean) {
    if (this.gridApi) {
      this.gridApi.setGridOption('loading', val);
    }
  }

  @Output() manageCategoryAccount = new EventEmitter<CategoryAccount>();
  @Output() deleteCategoryAccount = new EventEmitter<CategoryAccount>();
  @Output() updateCategoryAccounts = new EventEmitter<BatchUpdateCategoryAccountEntry[]>();

  private readonly snackbar = inject(TsSnackbarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly docRef = inject(DOCUMENT);
  private gridApi!: GridApi<CategoryAccount>;

  rowData: (CategoryAccount & WithPathAndDepth)[] = [];
  initialRowData: (CategoryAccount & WithPathAndDepth)[] = [];
  hasUnsavedChanges = new BehaviorSubject<boolean>(false);
  isOrderingRows$ = this.hasUnsavedChanges
    .asObservable()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((isOrdering) => {
      this.gridApi?.setColumnsVisible([TsTableComponent.ActionsColumnName], !isOrdering);
      this.gridApi?.refreshCells();
    });
  rowDraggingMode = new BehaviorSubject<'reorder' | 'nest'>('reorder');
  rowDraggingMode$ = this.rowDraggingMode
    .asObservable()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((val) => {
      // applies css classes
      this.gridApi?.refreshCells();
    });
  potentialTargetNode: IRowNode = null;

  get gridApiRowData() {
    return this.gridApi?.getGridOption('rowData');
  }

  get commonColumnDefinitionProps() {
    return {
      sortable: false,
      resizable: false,
      suppressHeaderMenuButton: true,
      cellStyle: { textTransform: 'capitalize' },
    };
  }

  readonly getDataPath = AssetManagementGridService.getDataPath;
  readonly autoGroupColumnDef: ColDef = {
    ...this.commonColumnDefinitionProps,
    rowDrag: true,
    headerName: 'Name',
    field: 'name',
    flex: 2,
    cellRendererParams: {
      suppressCount: true,
    },
    cellClassRules: {
      'hover-over--reorder': (params) => {
        return params.node === this.potentialTargetNode && this.rowDraggingMode.value === 'reorder';
      },
      'hover-over--nest': (params) => {
        return params.node === this.potentialTargetNode && this.rowDraggingMode.value === 'nest';
      },
    },
  };
  readonly colDefs: ColDef[] = [
    {
      ...this.commonColumnDefinitionProps,
      field: 'description',
      headerName: 'Description',
      flex: 1,
      valueFormatter: ({ data }) => {
        return data?.description || 'No description';
      },
      cellStyle: ({ data }) => {
        return data?.description ? { textTransform: 'capitalize' } : { fontStyle: 'italic', color: '#CCCCCC' };
      },
    },
    {
      ...this.commonColumnDefinitionProps,
      field: 'normal_balance',
      maxWidth: 140,
      headerName: 'Normal Balance',
    },
    {
      ...this.commonColumnDefinitionProps,
      field: 'report_type',
      maxWidth: 120,
      headerName: 'Report Type',
    },
    {
      ...this.commonColumnDefinitionProps,
      field: 'account_type',
      maxWidth: 140,
      headerName: 'Account Type',
    },
    TsTableComponent.getActionColumnDef([
      {
        icon: 'ts_pen',
        label: 'Edit Account',
        action: (data: CategoryAccount) => {
          this.manageCategoryAccount.emit(data);
        },
      },
      {
        icon: 'ts_trash-alt',
        label: 'Delete Account',
        variant: 'danger',
        action: (data: CategoryAccount) => {
          this.deleteCategoryAccount.emit(data);
        },
      },
    ]),
  ];
  readonly gridOptions = {
    loading: true,
    // keeps root accounts opened by default
    isGroupOpenByDefault: () => true,
  };

  private deepCloneCategoryAccounts(categoryAccounts: CategoryAccount[]) {
    // as long as we don't use dates or functions in the object, this is a safe way to clone the object
    return JSON.parse(JSON.stringify(categoryAccounts));
  }

  onGridReady(params: GridReadyEvent<CategoryAccount>): void {
    this.gridApi = params.api;
  }

  onRowDragMove(event: RowDragMoveEvent): void {
    // row dragged on top of itself
    if (event.overNode === event.node) {
      return;
    }

    if (this.isMouseOverLeftSideOfTable(event)) {
      this.rowDraggingMode.next('reorder');
    } else {
      this.rowDraggingMode.next('nest');
    }

    this.setPotentialTargetNode(event.api, event.overNode);
  }

  onRowDragLeave(event: RowDragLeaveEvent): void {
    // clear node to highlight
    this.clearNodeHighlight();
  }

  onRowDragEnd(event: RowDragEndEvent): void {
    if (this.isNoopRowMove(event.node)) {
      this.clearNodeHighlight();
      return;
    }

    const { data: movingData } = event.node;

    if (this.rowDraggingMode.value === 'nest') {
      this.handleRowsNesting(movingData, event);
    } else {
      this.gridApi.setGridOption('rowData', this.handleRowsReordering(event));
    }

    console.log(this.gridApiRowData);

    this.handleSaveOrRevertChanges(event);
  }

  private handleSaveOrRevertChanges(event: RowDragEndEvent<any, any>) {
    this.gridApi.clearFocusedCell();
    this.clearNodeHighlight();

    // display snackbar
    this.hasUnsavedChanges.next(true);
    this.snackbar.openApplyChanges(
      () => {
        this.gridApi.setGridOption('rowData', this.deepCloneCategoryAccounts(this.initialRowData));
        this.hasUnsavedChanges.next(false);
      },
      () => {
        // emit this result
        const result: BatchUpdateCategoryAccountEntry[] = [];
        this.gridApi.forEachNode(({ data }, index) =>
          result.push(<BatchUpdateCategoryAccountEntry>{
            id: data.id,
            name: data.name,
            parent_id: data.parent_id,
            rank: index,
          }),
        );
        this.hasUnsavedChanges.next(false);
        this.updateCategoryAccounts.emit(result);
      },
      'Category accounts have been updated',
    );
  }

  // TODO a node with children needs to update its path recursively for them
  // we are currently not supporting reordering of rows with children
  private handleRowsReordering(event: RowDragEndEvent<any, any>): CategoryAccount[] {
    const moveInArray = (arr: any[], fromIndex: number, toIndex: number) => {
      const result = [...arr];
      let fromElement = result[fromIndex];

      if (fromIndex < toIndex) {
        result.splice(fromIndex, 1);
        result.splice(toIndex - 1, 0, fromElement);
      } else {
        result.splice(fromIndex, 1);
        result.splice(toIndex, 0, fromElement);
      }

      return result;
    };

    let movingNode = event.node;
    let overNode = event.overNode;

    let rowNeedsToMove = movingNode !== overNode;

    if (rowNeedsToMove) {
      // the list of rows we have is data, not row nodes, so extract the data
      let movingData = movingNode.data;
      let overData = overNode!.data;

      // movingData needs to update its path to be the same as overData
      movingNode.data.path = [...overData.path];
      // movingData need to remove the sibling id add it's own id to the path
      movingNode.data.path.pop();
      movingNode.data.path.push(movingData.id);

      // movingData needs to update its parent_id to be the same as overData
      movingNode.data.parent_id = overData.parent_id;

      let fromIndex = this.gridApiRowData.indexOf(movingData);
      let toIndex = this.gridApiRowData.indexOf(overData);

      return moveInArray(this.gridApiRowData, fromIndex, toIndex).map((data, index) => ({
        ...data,
        rank: index,
      }));
    }
  }

  private handleRowsNesting(movingData, event: RowDragEndEvent<any, any>) {
    let newParentPath = this.potentialTargetNode.data ? this.potentialTargetNode.data.path : [];

    // take new parent path from parent, if data is missing, means it's the root node,
    // which has no data.
    let needToChangeParent = !this.arePathsEqual(newParentPath, movingData.path);

    if (needToChangeParent) {
      let updatedRows: any[] = [];
      this.moveToPath(newParentPath, event.node, updatedRows);
      this.gridApi.applyTransaction({
        update: updatedRows,
      });
    }
  }

  private isMouseOverLeftSideOfTable(event: RowDragMoveEvent<any, any>) {
    const tableSize = this.docRef.querySelector('.ag-body-viewport')?.getBoundingClientRect();
    // we use the data path length to calculate the responsiveness coefficient, that takes into account that a more
    // nested structure will require more space to trigger the reorder mode.
    const overNodeDataPathLength = event?.overNode?.data?.path?.length ?? 1;
    const responsivenessCoefficient = 0.1 * (1 + overNodeDataPathLength * 0.1);
    const reorderThreshold = window.innerWidth * responsivenessCoefficient;

    return tableSize?.left + reorderThreshold > event.event.clientX;
  }

  moveToPath(overNodePath: string[], { data: movingData, childrenAfterGroup }: IRowNode, allUpdatedNodes: any[]) {
    let oldPath = movingData.path;
    let movingNodeId = oldPath[oldPath.length - 1];

    let draggedCategoryNewPath = overNodePath.slice();
    draggedCategoryNewPath.push(movingNodeId);

    movingData.path = draggedCategoryNewPath;
    movingData.parent_id = overNodePath[overNodePath.length - 1];

    allUpdatedNodes.push(movingData);

    if (childrenAfterGroup) {
      childrenAfterGroup.forEach((childNode) => {
        this.moveToPath(draggedCategoryNewPath, childNode, allUpdatedNodes);
      });
    }
  }

  isSelectionParentOfTarget(selectedNode: IRowNode, targetNode: any) {
    let children = selectedNode.childrenAfterGroup || [];
    for (const element of children) {
      if (targetNode && element.key === targetNode.key) return true;
      this.isSelectionParentOfTarget(element, targetNode);
    }
    return false;
  }

  arePathsEqual(path1: string[], path2: string[]) {
    if (path1.length !== path2.length) {
      return false;
    }
    let equal = true;
    path1.forEach((item, index) => {
      if (path2[index] !== item) {
        equal = false;
      }
    });
    return equal;
  }

  private setPotentialTargetNode(api: GridApi, overNode: IRowNode | undefined | null) {
    let newPotentialTargetNode;
    if (overNode) {
      newPotentialTargetNode = overNode;
    } else {
      newPotentialTargetNode = null;
    }
    let alreadySelected = this.potentialTargetNode === newPotentialTargetNode;
    if (alreadySelected) {
      return;
    }
    // we refresh the previous selection (if it exists) to clear
    // the highlighted and then the new selection.
    let rowsToRefresh = [];
    if (this.potentialTargetNode) {
      rowsToRefresh.push(this.potentialTargetNode);
    }
    if (newPotentialTargetNode) {
      rowsToRefresh.push(newPotentialTargetNode);
    }
    this.potentialTargetNode = newPotentialTargetNode;
    this.refreshRows(api, rowsToRefresh);
  }

  private refreshRows(api: GridApi, rowsToRefresh: IRowNode[]) {
    let params: RefreshCellsParams = {
      // refresh these rows only.
      rowNodes: rowsToRefresh,
      // because the grid does change detection, the refresh
      // will not happen because the underlying value has not
      // changed. to get around this, we force the refresh,
      // which skips change detection.
      force: true,
    };
    api.refreshCells(params);
  }

  private isNoopRowMove(node: IRowNode<any>) {
    if (!this.potentialTargetNode) {
      return true;
    }

    if (this.isSelectionParentOfTarget(node, this.potentialTargetNode)) {
      console.warn('Invalid Row Move');
      return true;
    }

    if (node.allChildrenCount !== null) {
      console.warn('Rows with children cannot be moved');
      return true;
    }

    return false;
  }

  private clearNodeHighlight() {
    this.setPotentialTargetNode(this.gridApi, null);
  }

  private flattenCategoryAccountsAndAddMetadata(
    categoryAccounts: CategoryAccount[],
    path = [],
    depth = 0,
  ): (CategoryAccount & WithPathAndDepth)[] {
    return (
      categoryAccounts?.reduce(
        (acc, categoryAccount) => {
          const newPath = [...path, categoryAccount.id];

          acc.push({
            ...categoryAccount,
            path: newPath,
            depth,
          });

          if (categoryAccount?.children?.length > 0) {
            acc = acc.concat(this.flattenCategoryAccountsAndAddMetadata(categoryAccount.children, newPath, depth + 1));
          }

          return acc;
        },
        <(CategoryAccount & WithPathAndDepth)[]>[],
      ) ?? []
    );
  }
}
